import React, { useCallback, useMemo, useState } from 'react'
import { Badge } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { TanStackDatagrid, TanStackDatagridWithExport } from '../../../components'
import {
  TanStackDatagridColumn,
  TanStackDatagridPagination
} from '../../../components/TanStackDataGrid/TanStackDatagrid'
import {
  RESULTS_CHUNK_SIZE,
  resultsAdapter,
  setPageIndex,
  setPageSize,
  setPendingPageIndex
} from '../../../redux/slices/resultsSlice'
import { ResultEntity } from '../../../types/results'
import { DefaultState } from '../../../redux'
import ResultFilesModal from './ResultFilesModal/ResultFilesModal'
import { renderDateTime } from '../../../utils/dateFormatter'
import { capitalizeFirstLetter } from '../../../utils/helpers'
import {
  getAllResults,
  getResult,
  removeResult,
  updateResultAction
} from '../../../redux/slices'
import { AppDispatch } from '../../../services/lib'
import { IPAResultAction } from '../../../services/endpoints'
import ResultsActionCell from './ResultsActionCell'
import { showToast } from '../../../utils/types'

import './ResultsGrid.css'

const EMPTY_RESULT: ResultEntity = {
  id: '',
  analyzer: { id: '', name: '' },
  status: '',
  job_id: null,
  script: { id: '', name: '' },
  configs: [],
  files: [],
  created_at: '',
  updated_at: '',
  recycled: null,
  retention_lock: false
}

const selectResultsState = (state: DefaultState) => state.results
const { selectAll: selectAllResults } =
  resultsAdapter.getSelectors(selectResultsState)

const STATUS_BADGE_COLORS: Record<string, string> = {
  complete: 'success',
  finished: 'success',
  success: 'success',
  running: 'primary',
  queued: 'info',
  pending: 'warning',
  processing: 'warning',
  failed: 'danger',
  error: 'danger',
  canceled: 'secondary',
  cancelled: 'secondary'
}

const configurationDisplayValue = (data: ResultEntity): string =>
  data.configs && data.configs.length ? data.configs[0].name : '—'

type ResultsGridProps = {
  canManageResults: boolean
}

const ResultsGrid: React.FC<ResultsGridProps> = ({ canManageResults }) => {
  const results = useAppSelector(selectAllResults)
  const resultsError = useAppSelector((state) => state.results.error)
  const pageSize = useAppSelector((state) => state.results.pageSize)
  const pageIndex = useAppSelector((state) => state.results.pageIndex)
  const chunkOffset = useAppSelector((state) => state.results.offset)
  const chunkSize = useAppSelector((state) => state.results.chunkSize)
  const currentQuery = useAppSelector((state) => state.results.currentQuery)
  const loading = useAppSelector((state) => state.results.loading)
  const pendingPageIndex = useAppSelector((state) => state.results.pendingPageIndex)
  const total = useAppSelector((state) => state.results.total)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedResult, setSelectedResult] =
    useState<ResultEntity>(EMPTY_RESULT)
  const dispatch: AppDispatch = useDispatch()

  const visibleResults = useMemo(() => {
    if (!results.length) {
      return results
    }

    const normalizedPageSize = pageSize > 0 ? pageSize : results.length
    const startIndex = Math.max(0, pageIndex * normalizedPageSize - chunkOffset)
    const endIndex = startIndex + normalizedPageSize

    return results.slice(startIndex, endIndex)
  }, [results, pageIndex, pageSize, chunkOffset])

  const handleOpenFiles = useCallback((result: ResultEntity) => {
    setSelectedResult(result)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedResult(EMPTY_RESULT)
  }, [])

  const renderStatusCell = useCallback((data: ResultEntity) => {
    if (!data.status) {
      return '—'
    }

    const normalized = data.status.toLowerCase()
    const badgeColor = STATUS_BADGE_COLORS[normalized] || 'dark'
    const displayText = capitalizeFirstLetter(normalized)

    return (
      <Badge color={badgeColor} pill>
        {displayText}
      </Badge>
    )
  }, [])

  const renderFilesCell = useCallback(
    (data: ResultEntity) => {
      const fileCount = data.files.length

      if (!fileCount) {
        return '—'
      }

      return (
        <button
          type="button"
          className="result-files-link"
          onClick={() => handleOpenFiles(data)}
        >
          {`View ${fileCount} ${fileCount === 1 ? 'File' : 'Files'}`}
        </button>
      )
    },
    [handleOpenFiles]
  )

  const renderCreatedAtCell = useCallback(
    (data: ResultEntity) => renderDateTime({ value: data.created_at }),
    []
  )

  const renderUpdatedAtCell = useCallback(
    (data: ResultEntity) => renderDateTime({ value: data.updated_at }),
    []
  )

  const handleDelete = useCallback(
    async (result: ResultEntity) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete result ${result.id}?`
      )

      if (!confirmed) {
        return
      }

      await dispatch(removeResult(result.id))
    },
    [dispatch]
  )

  const handleResultAction = useCallback(
    async (result: ResultEntity, action: IPAResultAction) => {
      const response = await dispatch(
        updateResultAction({ id: result.id, action })
      )

      if (updateResultAction.rejected.match(response)) {
        const payload = response.payload as { message?: string } | undefined
        const message =
          payload?.message ||
          `Unable to ${action} result ${result.id}. Data was refreshed.`

        showToast(message, 'error', 3000)
        await dispatch(getResult(result.id))
      }
    },
    [dispatch]
  )

  const pagination = useMemo<TanStackDatagridPagination | undefined>(() => {
    if (!currentQuery || total === 0) {
      return undefined
    }

    return {
      pageIndex,
      pageSize,
      totalRows: total
    }
  }, [currentQuery, pageIndex, pageSize, total])

  const handlePaginationChange = useCallback(
    (nextPagination: TanStackDatagridPagination) => {
      if (!currentQuery || loading || pendingPageIndex !== null) {
        return
      }

      const nextPageSize = nextPagination.pageSize
      const nextPageIndex = nextPagination.pageIndex

      if (nextPageSize !== pageSize) {
        dispatch(setPageSize(nextPageSize))
      }

      if (nextPageIndex === pageIndex && nextPageSize === pageSize) {
        return
      }

      const effectiveChunkSize = chunkSize || RESULTS_CHUNK_SIZE
      const targetItemIndex = nextPageIndex * nextPageSize
      const chunkStart = chunkOffset
      const chunkEnd = chunkOffset + effectiveChunkSize

      if (targetItemIndex >= chunkStart && targetItemIndex < chunkEnd) {
        dispatch(setPageIndex(nextPageIndex))
        return
      }

      const requestQuery = {
        ...currentQuery,
        offset: Math.floor(targetItemIndex / effectiveChunkSize) * effectiveChunkSize,
        max: effectiveChunkSize
      }

      dispatch(setPendingPageIndex(nextPageIndex))
      dispatch(
        getAllResults({
          query: requestQuery,
          meta: { desiredPageIndex: nextPageIndex, replace: true }
        })
      )
    },
    [
      chunkOffset,
      chunkSize,
      currentQuery,
      dispatch,
      loading,
      pageIndex,
      pageSize,
      pendingPageIndex
    ]
  )

  const columns = useMemo<TanStackDatagridColumn<ResultEntity>[]>(
    () => [
      {
        id: 'actions',
        header: 'Action',
        accessor: () => '',
        cell: (result) => (
          <ResultsActionCell
            data={result}
            canManageResults={canManageResults}
            onDelete={handleDelete}
            onResultAction={handleResultAction}
          />
        ),
        resizable: true,
        hideable: false,
        width: 80
      },
      {
        id: 'job_id',
        header: 'Job ID',
        accessor: (result) => result.job_id ?? '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true,
        width: 60
      },
      {
        id: 'id',
        header: 'Result ID',
        accessor: (result) => result.id,
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'analyzerName',
        header: 'Analyzer',
        accessor: (result) => result.analyzer?.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true,
        minWidth: 120
      },
      {
        id: 'scriptName',
        header: 'Script',
        accessor: (result) => result.script?.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'configs',
        header: 'Configuration',
        accessor: configurationDisplayValue,
        resizable: true,
        hideable: true
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (result) => result.status || '',
        cell: renderStatusCell,
        resizable: true,
        hideable: true,
        width: 100
      },
      {
        id: 'created_at',
        header: 'Created',
        accessor: (result) => result.created_at || '',
        cell: renderCreatedAtCell,
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'updated_at',
        header: 'Updated',
        accessor: (result) => result.updated_at || '',
        cell: renderUpdatedAtCell,
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'files',
        header: 'Files',
        accessor: (result) => result.files.length,
        cell: renderFilesCell,
        resizable: true,
        hideable: true,
        width: 105
      }
    ],
    [
      canManageResults,
      handleDelete,
      handleResultAction,
      renderCreatedAtCell,
      renderFilesCell,
      renderStatusCell,
      renderUpdatedAtCell
    ]
  )

  return (
    <>
      <div className="resultsPage-grid">
        <div className="resultsPage-grid__container">
          <TanStackDatagridWithExport
            className="resultsPage-grid__datagrid"
            data={visibleResults}
            columns={columns}
            getRowId={(result) => result.id}
            emptyMessage="Run a search to view results."
            enablePagination={Boolean(pagination)}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
          />
        </div>
      </div>

      {isModalOpen && (
        <ResultFilesModal
          selectedResult={selectedResult}
          resultsError={resultsError || undefined}
          toggleModal={handleCloseModal}
          show={isModalOpen}
        />
      )}
    </>
  )
}

export default React.memo(ResultsGrid)
